import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../utils/api';
import { uploadDnaFile } from '../../utils/uploadDnaFile';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import { focusElement } from '../../utils/useFocusOnOpen';
import ConfirmDeleteModal from '../ConfirmDeleteModal';
import OrganicLoader from '../organic/OrganicLoader.jsx';
import { AttachedBioProfile } from '../bioprofile/BioProfile';

/*
 * What sequence analysis concluded about one DNA isolate (migration 0051,
 * routes/dna_identifications.py): one entry per marker and analysis, newest
 * first, as PITAX's import wrote them. Nothing here edits a conclusion. A
 * curator may add notes and references, attach a tree or an alignment made
 * with other tools (PITAX 3 writes neither), and withdraw a wrong entry with
 * a reason -- it stays on record, marked withdrawn.
 *
 * Writing follows the backend's dna.ELEVATED_ROLES, passed in as canEdit.
 */

// What the file pickers offer: routes/dna_files.py ROLE_EXTENSIONS, a
// compressed alignment by its whole extension (.aln.gz -- never a bare .gz,
// which would offer reads.fastq.gz). The server refuses anything else, so
// the picker offers exactly this (test_dna_identifications.py holds them equal).
export const TREE_ACCEPT = '.nwk,.newick,.tree,.tre,.nex,.nexus,.txt';
export const ALIGNMENT_ACCEPT = '.fasta,.fa,.fas,.aln,.phy,.phylip,.nex,.txt,'
  + '.fasta.gz,.fa.gz,.fas.gz,.aln.gz,.phy.gz,.phylip.gz,.nex.gz,.txt.gz';
const LINEAGE = ['kingdom', 'phylum', 'class_name', 'order_name', 'family', 'genus', 'species'];
// Refusals the panel says in its own words (identifications.errors.*); any
// other gets the action's own translated failure, never the server's English.
const ERROR_CODES = ['invalid_text', 'reason_required', 'already_withdrawn', 'withdrawn', 'invalid_file_role',
  'invalid_key', 'invalid_extension', 'identification_not_found', 'not_editable', 'nothing_to_change',
  'dna_not_found'];
const MAX_PROSE = 4000;
const MIN_REASON = 3;

/** The GenBank record of a BLAST match, at NCBI. */
export function ncbiUrl(accession) {
  const clean = String(accession || '').trim();
  return clean ? `https://www.ncbi.nlm.nih.gov/nuccore/${encodeURIComponent(clean)}` : null;
}

/** "Pleurotus eryngii" as "P. eryngii"; anything that is not a binomial as it is. */
export function shortName(name) {
  const text = String(name || '').trim();
  const words = text.split(/\s+/);
  if (words.length >= 2 && /^[A-Z][a-z-]+$/.test(words[0]) && /^[a-z]/.test(words[1])) {
    return `${words[0][0]}. ${words.slice(1).join(' ')}`;
  }
  return text;
}

/** A taxonomic rank as taxize names it (family, suborder, "no rank" ...) in the reader's words. */
export function rankText(rank, t) {
  const raw = String(rank || '').trim();
  const key = raw.toLowerCase().replace(/[^a-z]/g, '');
  return key ? t(`identifications.ranks.${key}`, { defaultValue: raw }) : '';
}

/** PITAX's level (species | genus | unresolved | "LCA: <rank>") in the reader's words. */
export function levelText(level, t) {
  const raw = String(level || '').trim();
  if (!raw) return '';
  const lca = raw.match(/^LCA\s*:\s*(.+)$/i);
  if (lca) return t('identifications.levels.lca', { rank: rankText(lca[1], t), defaultValue: raw });
  const key = raw.toLowerCase();
  return ['species', 'genus', 'unresolved'].includes(key)
    ? t(`identifications.levels.${key}`, { defaultValue: raw }) : raw;
}

/** A consensus status (READY, REVIEW_REQUIRED ...) in the reader's words. */
export function consensusStatusText(status, t) {
  const raw = String(status || '').trim();
  return raw ? t(`identifications.consensusStatus.${raw.toUpperCase()}`, { defaultValue: raw }) : '';
}

/**
 * What PITAX called it, for display: { name } when it named a taxon,
 * otherwise { text } -- "not resolved" for PITAX's own "Unresolved" (not a
 * name, and the level would only say it again: showLevel false), else "no
 * identification".
 */
export function theCall(item, t) {
  const name = String(item?.recommended_identification || '').trim();
  const level = String(item?.recommended_level || '').trim().toLowerCase();
  if (name.toLowerCase() === 'unresolved' || (!name && level === 'unresolved')) {
    return { name: '', text: t('identifications.unresolved'), showLevel: false };
  }
  if (!name) return { name: '', text: t('identifications.noCall'), showLevel: true };
  return { name, text: name, showLevel: true };
}

/** PITAX's confidence (High | Moderate | Low / review) in the reader's words. */
export function confidenceText(confidence, t) {
  const raw = String(confidence || '').trim();
  const key = raw.toLowerCase().startsWith('high') ? 'high'
    : raw.toLowerCase().startsWith('moderate') ? 'moderate'
      : raw.toLowerCase().startsWith('low') ? 'low' : null;
  return key ? t(`identifications.confidenceLevels.${key}`, { defaultValue: raw }) : raw;
}

/** "P. eryngii · species · High": the isolate list's one line for list_dna's latest_identification. */
export function latestSummary(latest, t) {
  if (!latest) return '';
  const call = theCall(latest, t);
  return [call.name ? shortName(call.name) : call.text, call.showLevel ? levelText(latest.recommended_level, t) : '',
    confidenceText(latest.confidence, t)].filter(Boolean).join(' · ');
}

/**
 * The same line as JSX: a Latin name in italics and kept left to right (in
 * Hebrew too); the rest -- "not resolved" included -- in the page's words
 * and direction.
 */
export function LatestIdentification({ latest, t }) {
  const call = theCall(latest, t);
  const level = call.showLevel ? levelText(latest.recommended_level, t) : '';
  const confidence = confidenceText(latest.confidence, t);
  return (
    <span data-testid="dna-latest-identification">
      {call.name
        ? <bdi dir="ltr" style={{ fontStyle: 'italic' }}>{shortName(call.name)}</bdi>
        : <span>{call.text}</span>}
      {level && <> · {level}</>}
      {confidence && <> · {confidence}</>}
    </span>
  );
}

const percent = (v) => (v === null || v === undefined || v === '' ? null : `${v}%`);
const when = (iso) => (iso ? new Date(iso).toLocaleString() : null);

export default function DnaIdentificationsPanel({ dnaId, isolateCode, canEdit, onClose, onChanged }) {
  const { t } = useTranslation(['dna', 'common']);
  const headingRef = useRef(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notes, setNotes] = useState(null);          // { id, curator_notes, references_text } while editing
  const [notesSaving, setNotesSaving] = useState(false);
  const [cardError, setCardError] = useState(null);  // { id, text }
  const [uploading, setUploading] = useState('');    // '<id>:<role>' while a file goes up
  const [withdrawTarget, setWithdrawTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [withdrawError, setWithdrawError] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);

  const errorText = (data, fallbackKey) => (ERROR_CODES.includes(data?.code)
    ? t(`identifications.errors.${data.code}`) : t(fallbackKey));
  // Gone (deleted, or no longer assigned to a student) or not loaded, in the reader's words.
  const loadErrorText = (err) => t(err?.response?.status === 404 ? 'identifications.notFound'
    : 'identifications.loadFailed');

  const fetchItems = () => Promise.resolve(api.get(`/dna/${dnaId}/identifications`))
    .then(res => { setItems(res?.data?.identifications || []); setLoadError(''); })
    .catch(err => setLoadError(loadErrorText(err)));

  useEffect(() => {
    let live = true;
    setLoading(true);
    setItems([]);
    setNotes(null);
    setCardError(null);
    Promise.resolve(api.get(`/dna/${dnaId}/identifications`))
      .then(res => { if (live) { setItems(res?.data?.identifications || []); setLoadError(''); } })
      .catch(err => { if (live) setLoadError(loadErrorText(err)); })
      .finally(() => { if (live) setLoading(false); });
    // The panel opens below the isolates; focus follows it there.
    focusElement(headingRef.current);
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dnaId]);

  // A reply carries the row as stored; whether it is the marker's standing
  // answer is the list's to say, so that is kept.
  const replace = (row) => setItems(list => list.map(i => (i.identification_id === row.identification_id
    ? { ...i, ...row } : i)));

  const saveNotes = async () => {
    setNotesSaving(true);
    setCardError(null);
    try {
      const { data } = await api.put(`/dna/identifications/${notes.id}`, {
        curator_notes: notes.curator_notes, references_text: notes.references_text,
      });
      replace(data);
      setNotes(null);
    } catch (err) {
      setCardError({ id: notes.id, text: errorText(err?.response?.data, 'identifications.notesFailed') });
    } finally {
      setNotesSaving(false);
    }
  };

  const attach = async (item, role, file) => {
    if (!file) return;
    const id = item.identification_id;
    setUploading(`${id}:${role}`);
    setCardError(null);
    try {
      const { s3_key } = await uploadDnaFile(file, dnaId, item.gene_name || item.marker, role);
      const { data } = await api.post(`/dna/identifications/${id}/files`, { file_role: role, key: s3_key });
      replace(data);
    } catch (err) {
      setCardError({ id, text: errorText(err?.response?.data, 'identifications.attachFailed') });
    } finally {
      setUploading('');
    }
  };

  const openWithdraw = (item) => { setWithdrawTarget(item); setReason(''); setWithdrawError(''); };

  const withdraw = async () => {
    if (reason.trim().length < MIN_REASON) {
      setWithdrawError(t('identifications.reasonTooShort'));
      return;
    }
    setWithdrawing(true);
    setWithdrawError('');
    try {
      await api.post(`/dna/identifications/${withdrawTarget.identification_id}/withdraw`, { reason: reason.trim() });
      setWithdrawTarget(null);
      // Which answer stands for the marker may have changed: read them again,
      // and let the isolate list update its one line.
      await fetchItems();
      if (onChanged) onChanged();
    } catch (err) {
      setWithdrawError(errorText(err?.response?.data, 'identifications.withdrawFailed'));
    } finally {
      setWithdrawing(false);
    }
  };

  return (
    <section id={`dna-identifications-${dnaId}`} data-testid="dna-identifications" style={panelStyle}
             aria-labelledby={`dna-identifications-title-${dnaId}`}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <h3 ref={headingRef} tabIndex={-1} id={`dna-identifications-title-${dnaId}`} style={{ margin: 0, fontSize: 16, overflowWrap: 'anywhere' }}>
          {t('identifications.title')} · <bdi dir="ltr">{isolateCode}</bdi>
        </h3>
        <button type="button" onClick={onClose} title={t('tip.closeIdentifications')} style={plainBtn}>
          {t('identifications.close')}
        </button>
      </div>
      <p style={mutedStyle}>{t('identifications.intro')}</p>

      {loading && <OrganicLoader inline showLabel label={t('common:loading')} slowLabel={t('common:stillWorking')} />}
      {loadError && <p role="alert" style={errorStyle}>{loadError}</p>}
      {!loading && !loadError && items.length === 0 && (
        <p style={mutedStyle} data-testid="dna-identifications-none">{t('identifications.none')}</p>
      )}

      {items.length > 0 && (
        <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
          {items.map(item => (
            <IdentificationCard key={item.identification_id} item={item} t={t} canEdit={canEdit}
              notes={notes?.id === item.identification_id ? notes : null}
              onEditNotes={() => { setCardError(null); setNotes({ id: item.identification_id,
                curator_notes: item.curator_notes || '', references_text: item.references_text || '' }); }}
              onNotesChange={(field, value) => setNotes(n => ({ ...n, [field]: value }))}
              onSaveNotes={saveNotes} onCancelNotes={() => setNotes(null)} notesSaving={notesSaving}
              error={cardError?.id === item.identification_id ? cardError.text : ''}
              uploading={uploading} onAttach={(role, file) => attach(item, role, file)}
              onWithdraw={() => openWithdraw(item)} />
          ))}
        </ul>
      )}

      <ConfirmDeleteModal
        open={!!withdrawTarget}
        title={t('identifications.withdrawTitle')}
        consequences={withdrawTarget ? [t('identifications.withdrawBody')] : []}
        deleting={withdrawing}
        error={withdrawError}
        confirmLabel={t('identifications.withdraw')}
        confirmingLabel={t('identifications.withdrawing')}
        onConfirm={withdraw}
        onCancel={() => { if (!withdrawing) setWithdrawTarget(null); }}
      >
        <label style={{ ...labelStyle, marginBottom: 12 }}>
          {t('identifications.withdrawReason')}
          <textarea value={reason} onChange={e => { setReason(e.target.value); setWithdrawError(''); }} rows={3}
                    maxLength={MAX_PROSE} aria-required="true" data-testid="dna-identification-withdraw-reason"
                    title={t('tip.withdrawReason')} style={inputStyle} />
        </label>
      </ConfirmDeleteModal>
    </section>
  );
}

function IdentificationCard({ item, t, canEdit, notes, onEditNotes, onNotesChange, onSaveNotes, onCancelNotes,
  notesSaving, error, uploading, onAttach, onWithdraw }) {
  const treeInput = useRef(null);
  const alignmentInput = useRef(null);
  const id = item.identification_id;
  const writable = canEdit && !item.withdrawn;
  const lineage = LINEAGE.map(k => item[k]).filter(Boolean);
  const link = ncbiUrl(item.best_match_accession);
  const versions = [item.tool_version && `PITAX ${item.tool_version}`,
    item.algorithm_version && t('identifications.algorithm', { version: item.algorithm_version })].filter(Boolean);
  const call = theCall(item, t);
  // The consensus id and its length are codes, left to right; its status is words, in the page's direction.
  const consensus = [
    item.consensus_id && <bdi key="id" dir="ltr">{item.consensus_id}</bdi>,
    item.consensus_status && <span key="status" data-testid="dna-identification-consensus-status">
      {consensusStatusText(item.consensus_status, t)}</span>,
    item.sequence_length != null && <bdi key="bp" dir="ltr">{t('identifications.bp', { value: item.sequence_length })}</bdi>,
  ].filter(Boolean);
  const status = item.withdrawn ? 'withdrawn' : item.current ? 'current' : 'superseded';
  const why = [['decisionReason', item.decision_reason], ['closestAlternative', item.closest_alternative],
    ['taxonomicSupport', item.taxonomic_support], ['referenceSupport', item.reference_support],
    ['locusDiscrimination', item.locus_discrimination]].filter(([, v]) => v);

  const fact = (key, value, testId) => (value === null || value === undefined || value === '' ? null : (
    <div style={{ minWidth: 0 }} data-testid={testId}>
      <dt style={dtStyle}>{t(`identifications.${key}`)}</dt>
      <dd style={ddStyle}>{value}</dd>
    </div>
  ));

  return (
    <li data-testid={`dna-identification-${id}`} style={cardStyle(item.withdrawn)}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}>
        <span dir="ltr" style={markerStyle} title={t('identifications.marker')}>{item.gene_name || item.marker}</span>
        <strong style={{ fontSize: 15, overflowWrap: 'anywhere', textDecoration: item.withdrawn ? 'line-through' : 'none' }}>
          {call.name ? <bdi dir="ltr" style={{ fontStyle: 'italic' }}>{call.name}</bdi> : call.text}
        </strong>
        <span style={statusStyle(status)} data-testid="dna-identification-status">{t(`identifications.${status}`)}</span>
      </div>

      <dl style={factsStyle}>
        {call.showLevel && fact('level', levelText(item.recommended_level, t))}
        {fact('confidence', confidenceText(item.confidence, t))}
        {fact('bestMatch', (item.best_match_organism || link) ? (
          <>
            {item.best_match_organism && <bdi dir="ltr" style={{ fontStyle: 'italic' }}>{item.best_match_organism}</bdi>}
            {link && (
              <>
                {item.best_match_organism ? ' ' : ''}
                <a href={link} target="_blank" rel="noopener noreferrer" className="touch-target" dir="ltr"
                   title={t('tip.ncbiLink')} data-testid="dna-identification-ncbi" style={linkStyle}>
                  {item.best_match_accession}
                </a>
              </>
            )}
          </>
        ) : null)}
        {fact('identity', percent(item.identity_pct) && <bdi dir="ltr">{percent(item.identity_pct)}</bdi>)}
        {fact('coverage', percent(item.coverage_pct) && <bdi dir="ltr">{percent(item.coverage_pct)}</bdi>)}
        {fact('alignmentLength', item.alignment_length != null
          ? <bdi dir="ltr">{t('identifications.bp', { value: item.alignment_length })}</bdi> : null)}
        {fact('lineage', lineage.length ? <bdi dir="ltr" style={{ overflowWrap: 'anywhere' }}>{lineage.join(' › ')}</bdi> : null,
          'dna-identification-lineage')}
        {fact('consensus', consensus.length ? (
          <span style={{ overflowWrap: 'anywhere' }}>
            {consensus.map((part, i) => <span key={part.key}>{i > 0 && ' · '}{part}</span>)}
          </span>
        ) : null)}
        {fact('run', item.run_code ? <bdi dir="ltr" style={{ overflowWrap: 'anywhere' }}>{item.run_code}</bdi> : null)}
        {fact('version', versions.length ? <bdi dir="ltr">{versions.join(' · ')}</bdi> : null)}
        {fact('analyzedAt', when(item.analyzed_at))}
      </dl>

      {/* A sequence-based answer is a verified identification: its taxon's whole
          profile, as it was attached when the answer was imported. */}
      {item.current && item.recommended_identification
        && ['species', 'genus', 'family'].includes(String(item.recommended_level || '').toLowerCase()) && (
        <AttachedBioProfile stored={item.bioprofile} verified name={item.recommended_identification}
                            testId={`dna-identification-bioprofile-${id}`} />
      )}

      {why.length > 0 && (
        <details style={{ marginTop: 6, fontSize: 13 }}>
          <summary style={{ cursor: 'pointer', color: 'var(--c-primary-fg)' }}>{t('identifications.more')}</summary>
          <dl style={{ margin: '6px 0 0' }}>
            {why.map(([key, value]) => (
              <div key={key} style={{ marginBottom: 4 }}>
                <dt style={dtStyle}>{t(`identifications.${key}`)}</dt>
                <dd style={{ ...ddStyle, whiteSpace: 'pre-wrap' }} dir="auto">{value}</dd>
              </div>
            ))}
          </dl>
        </details>
      )}

      {item.withdrawn && (
        <p style={{ ...mutedStyle, color: 'var(--c-danger-fg)' }} data-testid="dna-identification-withdrawn">
          {t('identifications.withdrawnBy', { user: item.withdrawn_by_username || '—', date: when(item.withdrawn_at) || '—',
            reason: item.withdraw_reason || '' })}
        </p>
      )}

      {notes ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
          <label style={labelStyle}>
            {t('identifications.curatorNotes')}
            <textarea value={notes.curator_notes} rows={3} maxLength={MAX_PROSE} dir="auto"
                      onChange={e => onNotesChange('curator_notes', e.target.value)}
                      data-testid="dna-identification-notes-input" title={t('tip.curatorNotes')} style={inputStyle} />
          </label>
          <label style={labelStyle}>
            {t('identifications.references')}
            <textarea value={notes.references_text} rows={2} maxLength={MAX_PROSE} dir="auto"
                      onChange={e => onNotesChange('references_text', e.target.value)}
                      data-testid="dna-identification-references-input" title={t('tip.references')} style={inputStyle} />
          </label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            <button type="button" onClick={onSaveNotes} disabled={notesSaving} title={t('tip.saveNotes')}
                    data-testid="dna-identification-notes-save" style={btnStyle('var(--c-primary)')}>
              {notesSaving ? t('common:saving') : t('identifications.saveNotes')}
            </button>
            <button type="button" onClick={onCancelNotes} disabled={notesSaving} title={t('tip.cancelNotes')}
                    style={btnStyle('var(--c-text-muted)')}>
              {t('common:cancel')}
            </button>
          </div>
        </div>
      ) : (
        <>
          {item.curator_notes && (
            <div style={{ marginTop: 6 }}>
              <div style={dtStyle}>{t('identifications.curatorNotes')}</div>
              <p style={proseStyle} dir="auto" data-testid="dna-identification-notes">{item.curator_notes}</p>
            </div>
          )}
          {item.references_text && (
            <div style={{ marginTop: 6 }}>
              <div style={dtStyle}>{t('identifications.references')}</div>
              <p style={proseStyle} dir="auto">{item.references_text}</p>
            </div>
          )}
        </>
      )}

      {/* Re-based onto this client's server: on the desktop a file URL names
          127.0.0.1, which on a phone is the phone. */}
      {(item.tree_file_url || item.alignment_file_url) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 6, fontSize: 13 }}>
          {item.tree_file_url && (
            <a href={resolvePhotoUrl(item.tree_file_url)} target="_blank" rel="noopener noreferrer" download className="touch-target"
               title={t('tip.treeFile')} data-testid="dna-identification-tree-link" style={linkStyle}>
              ⬇ {t('identifications.treeFile')}
            </a>
          )}
          {item.alignment_file_url && (
            <a href={resolvePhotoUrl(item.alignment_file_url)} target="_blank" rel="noopener noreferrer" download className="touch-target"
               title={t('tip.alignmentFile')} data-testid="dna-identification-alignment-link" style={linkStyle}>
              ⬇ {t('identifications.alignmentFile')}
            </a>
          )}
        </div>
      )}

      {error && <p role="alert" style={errorStyle}>{error}</p>}

      {writable && !notes && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
          <button type="button" onClick={onEditNotes} title={t('tip.editNotes')}
                  data-testid="dna-identification-edit-notes" style={smallBtn}>
            {t('identifications.editNotes')}
          </button>
          <button type="button" onClick={() => treeInput.current?.click()} disabled={!!uploading}
                  title={t('tip.attachTree')} data-testid="dna-identification-attach-tree" style={smallBtn}>
            {uploading === `${id}:tree` ? t('identifications.attaching') : t('identifications.attachTree')}
          </button>
          <input ref={treeInput} type="file" accept={TREE_ACCEPT} style={{ display: 'none' }}
                 aria-label={t('identifications.attachTree')} data-testid="dna-identification-tree-input"
                 onChange={e => { onAttach('tree', e.target.files?.[0]); e.target.value = ''; }} />
          <button type="button" onClick={() => alignmentInput.current?.click()} disabled={!!uploading}
                  title={t('tip.attachAlignment')} data-testid="dna-identification-attach-alignment" style={smallBtn}>
            {uploading === `${id}:alignment` ? t('identifications.attaching') : t('identifications.attachAlignment')}
          </button>
          <input ref={alignmentInput} type="file" accept={ALIGNMENT_ACCEPT} style={{ display: 'none' }}
                 aria-label={t('identifications.attachAlignment')} data-testid="dna-identification-alignment-input"
                 onChange={e => { onAttach('alignment', e.target.files?.[0]); e.target.value = ''; }} />
          <button type="button" onClick={onWithdraw} title={t('tip.withdrawIdentification')}
                  data-testid="dna-identification-withdraw" style={dangerBtn}>
            {t('identifications.withdraw')}
          </button>
        </div>
      )}
    </li>
  );
}

const panelStyle = {
  background: 'var(--c-bg-subtle)', border: '1px solid var(--c-border)', borderRadius: 8,
  padding: 14, marginBottom: 16, minWidth: 0,
};
const cardStyle = (withdrawn) => ({
  background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 8, padding: 12,
  borderInlineStart: `4px solid ${withdrawn ? 'var(--c-danger-border)' : 'var(--c-primary)'}`, minWidth: 0,
});
const factsStyle = {
  display: 'grid',
  // One column on a phone, never wider than the card.
  gridTemplateColumns: 'repeat(auto-fill, minmax(min(180px, 100%), 1fr))',
  gap: '6px 14px', margin: '8px 0 0',
};
const dtStyle = { fontSize: 12, color: 'var(--c-text-muted-fg)', fontWeight: 600 };
const ddStyle = { margin: 0, fontSize: 14, overflowWrap: 'anywhere' };
const proseStyle = { margin: '2px 0 0', fontSize: 14, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' };
const mutedStyle = { fontSize: 13, color: 'var(--c-text-muted-fg)', margin: '6px 0' };
const errorStyle = { fontSize: 13, color: 'var(--c-danger-fg)', margin: '6px 0' };
const markerStyle = {
  fontSize: 12, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
  background: 'var(--c-primary-soft)', color: 'var(--c-primary-fg)',
};
const statusStyle = (status) => ({
  fontSize: 12, padding: '2px 8px', borderRadius: 10, fontWeight: 600,
  background: status === 'withdrawn' ? 'var(--c-danger-soft)' : status === 'current' ? 'var(--c-success-soft)' : 'var(--c-bg-muted)',
  color: status === 'withdrawn' ? 'var(--c-danger-fg)' : status === 'current' ? 'var(--c-success-fg)' : 'var(--c-text-muted-fg)',
});
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14, fontWeight: 500 };
const inputStyle = {
  padding: '6px 10px', border: '1px solid var(--c-border-strong)', borderRadius: 6, fontSize: 14,
  fontWeight: 400, width: '100%', fontFamily: 'inherit',
};
const linkStyle = { color: 'var(--c-primary-fg)', display: 'inline-flex', alignItems: 'center' };
const btnStyle = (bg) => ({
  background: bg, color: 'var(--c-on-accent)', border: 'none', borderRadius: 6, padding: '8px 16px',
  cursor: 'pointer', fontSize: 14,
});
const smallBtn = {
  background: 'var(--c-surface)', color: 'var(--c-primary-fg)', border: '1px solid var(--c-primary)',
  borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 13,
};
const dangerBtn = {
  background: 'var(--c-surface)', color: 'var(--c-danger-2-fg)', border: '1px solid var(--c-danger-border)',
  borderRadius: 6, padding: '5px 10px', cursor: 'pointer', fontSize: 13,
};
const plainBtn = {
  background: 'none', border: '1px solid var(--c-border-strong)', borderRadius: 6, padding: '4px 10px',
  cursor: 'pointer', fontSize: 13, color: 'var(--c-text-fg)',
};
